import React, { useState, useEffect } from 'react';
import axios from 'axios';
import packageJson from '../package.json';
import 'bootstrap/dist/css/bootstrap.min.css';
import './App.css';
import { signOut, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { Route, Routes, Link, Navigate, useLocation, useNavigate } from 'react-router-dom';
import AdminPage from './AdminPage';
import { Button, Avatar, AppBar, Toolbar, Typography, Box, Chip, Tooltip, Alert, IconButton, ThemeProvider, createTheme, CssBaseline, TableContainer, Table, TableHead, TableBody, TableRow, TableCell, Paper, CircularProgress, Tabs, Tab, LinearProgress } from '@mui/material';
import { Person, ExitToApp, AdminPanelSettings, History, ExpandMore, ExpandLess, Refresh, Brightness4, Brightness7, Favorite, FavoriteBorder, Delete, Search, HelpOutline, InfoOutlined } from '@mui/icons-material';
import { auth, signInWithGoogle, refreshAdminStatus, getIdToken, saveQueryToHistory, getQueryHistory, saveQueryHistoryVisibility, getQueryHistoryVisibility, saveDarkModePreference, getDarkModePreference, db, saveQuery, getAllQueries, getFollowedQueries, unfollowQuery, followQuery, deleteQuery, refreshQuery } from './firebase';
import RequireAuth from './components/RequireAuth';

axios.defaults.timeout = 10000; // 10 saniye

// API URL'yi environment'tan al
const API_URL = process.env.REACT_APP_API_URL || 'https://api-got6faywaa-uc.a.run.app';

// Gereksiz development loglama kodlarını kaldır
// if (process.env.NODE_ENV === 'development') {
//   // Geliştirici araçları için öneriler
// }

export const handleQuery = async (domain, queryType, idToken) => {
  try {
    const headers = {
      'Content-Type': 'application/json'
    };
    
    // Eğer token varsa kullan
    if (idToken) {
      headers['Authorization'] = `Bearer ${idToken}`;
    }

    // API isteğini gönder
    const response = await axios.post(
      `${API_URL}/api/dns`,
      { domain, queryType },
      { headers }
    );
    return response.data;
  } catch (error) {
    if (error.response && error.response.data) {
      throw new Error(error.response.data.error || 'Sunucu hatası');
    } else if (error.request) {
      if (error.code === 'ECONNABORTED') {
        throw new Error('Sunucu yanıt vermedi. Lütfen daha sonra tekrar deneyin.');
      }
      throw new Error(error.message || 'Sunucuya ulaşılamadı. Lütfen internet bağlantınızı kontrol edin.');
    } else {
      throw new Error(error.message || 'Bir hata oluştu. Lütfen tekrar deneyin.');
    }
  }
};

// Hata durumunda detaylı loglama
const handleError = (error, context) => {
  // Gereksiz development loglama kodlarını kaldır
  // if (process.env.NODE_ENV === 'development') {
  //   // Sadece kritik hataları logla
  //   if (error.response && error.response.status >= 500) {
  //     console.error(`Server Error in ${context}:`, error.response.status);
  //   }
  // }
};

function App() {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [domain, setDomain] = useState('');
  const [queryType, setQueryType] = useState('gmail');
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('TXT');
  const [darkMode, setDarkMode] = useState(false);
  const [queries, setQueries] = useState([]);
  const [followedQueries, setFollowedQueries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState({});
  const [activeMainTab, setActiveMainTab] = useState('new');
  const [refreshProgress, setRefreshProgress] = useState(0);
  const [autoQueryFlag, setAutoQueryFlag] = useState(false);

  const theme = createTheme({
    palette: {
      mode: darkMode ? 'dark' : 'light',
      background: {
        default: darkMode ? '#121212' : '#f8f9fa',
        paper: darkMode ? '#1e1e1e' : '#ffffff',
      },
      text: {
        primary: darkMode ? '#e0e0e0' : '#000000',
      },
    },
    typography: {
      fontSize: 13,
      h1: { fontSize: '1.5rem' },
      h2: { fontSize: '1.3rem' },
      h3: { fontSize: '1.1rem' },
      body1: { fontSize: '0.9rem' },
      body2: { fontSize: '0.8rem' },
    },
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            padding: '4px 12px',
          },
        },
      },
    },
  });

  const location = useLocation();
  const navigate = useNavigate();

  // Sorguları yükle
  const loadQueries = async () => {
    if (!auth.currentUser) return;
    
    try {
      setLoading(true);
      
      const [allQueries, followed] = await Promise.all([
        getAllQueries(auth.currentUser.uid),
        getFollowedQueries(auth.currentUser.uid)
      ]);

      // Takip edilen domain'leri bir Set'e al
      const followedDomains = new Set(followed.map(q => q.domain));

      // Tüm sorgulara takip durumunu ekle
      const queriesWithFollowStatus = allQueries.map(query => ({
        ...query,
        isFollowed: followedDomains.has(query.domain)
      }));

      // Debug log kaldırıldı
      setQueries(queriesWithFollowStatus);
      setFollowedQueries(followed);
    } catch (err) {
      // console.error yerine setError kullan
      setError('Sorgular yüklenirken hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const performQuery = async (domain, queryType) => {
    if (!domain) return;
    
    // Hata mesajını temizle ama mevcut sonuçları koruyalım
    setError(null);
    
    // Loading durumunu aktif et ama sonuçları silme
    setLoading(true);
    
    try {
      let token = null;
      
      // Kullanıcı giriş yapmışsa token al
      if (auth.currentUser) {
        try {
          token = await getIdToken();
        } catch (tokenError) {
          console.error('Token alınırken hata:', tokenError);
          // Token hatası olsa bile sorguya devam edebiliriz
        }
      }
      
      // URL'i güncelle
      updateURL(domain, queryType);
      
      // Sorguyu yap (token varsa kullan)
      const result = await handleQuery(domain, queryType, token);
      
      // Kullanıcı giriş yapmışsa sorguyu veritabanına kaydet
      if (auth.currentUser) {
        try {
          const savedData = await saveQuery(auth.currentUser.uid, {
            domain,
            ...result,
            queryType,
            timestamp: new Date(),
            lastChecked: new Date()
          });
          
          // Kaydedilen veriyi göster
          setResults(savedData);
          
          // Sorguları arka planda yükle, sonuçları beklemeden devam et
          loadQueries().catch(err => console.error('Sorgular yüklenirken hata:', err));
        } catch (saveError) {
          console.error('Sorgu kaydedilirken hata:', saveError);
          // Kaydetme hatası olsa bile sonuçları göster
          setResults(result);
        }
      } else {
        // Giriş yapmamış kullanıcılar için direkt sonucu göster
        setResults(result);
      }
    } catch (error) {
      console.error('Sorgu hatası:', error);
      setError(error.message || 'Bir hata oluştu');
    } finally {
      // Loading durumunu kapat
      setLoading(false);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUser(user);
        const userDocRef = doc(db, "users", user.uid);
        const userDocSnap = await getDoc(userDocRef);
        
        if (!userDocSnap.exists()) {
          await setDoc(userDocRef, {
            email: user.email,
            isAdmin: false,
            darkMode: false
          });
        }
        
        const adminStatus = await refreshAdminStatus(user);
        setIsAdmin(adminStatus);
        
        const darkModePref = await getDarkModePreference(user.uid);
        setDarkMode(darkModePref);
        
        // Kullanıcı giriş yaptığında sorguları yükle
        loadQueries();
      } else {
        setUser(null);
        setIsAdmin(false);
        setDarkMode(false);
      }
    });

    return () => unsubscribe();
  }, []);
  
  // Sayfa yüklendiğinde sorguları yükle
  useEffect(() => {
    if (auth.currentUser) {
      loadQueries();
    }
  }, []);

  // URL parametrelerini işleme fonksiyonu
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const domainParam = params.get('domain');
    const formParam = params.get('form');

    if (domainParam) {
      setDomain(domainParam);
      
      // form parametresini queryType'a dönüştür
      if (formParam) {
        const queryTypeMap = {
          'gmail': 'gmail',
          'dns': 'dns',
          'whois': 'whois'
        };
        
        const newQueryType = queryTypeMap[formParam.toLowerCase()] || 'gmail';
        setQueryType(newQueryType);
      }
      
      // Sayfa yüklendiğinde otomatik sorgu yapılması için bir flag ayarla
      setAutoQueryFlag(true);
    }
  }, [location.search]);
  
  // Otomatik sorgu için ayrı bir useEffect
  useEffect(() => {
    if (autoQueryFlag && domain) {
      // Auth durumunun hazır olduğundan emin olmak için bir kontrol mekanizması ekle
      const checkAuthAndQuery = async () => {
        // Auth durumunu kontrol et, giriş yapılmış olsa da olmasa da sorgu yapılabilmeli
        try {
          // URL'i hemen güncelle
          updateURL(domain, queryType);
          
          // Sorguyu yap, token kullanma
          performQuery(domain, queryType);
        } catch (error) {
          // console.error yerine setError kullan
          setError('Otomatik sorgu başlatılırken hata oluştu. Lütfen manuel olarak tekrar deneyin.');
        } finally {
          // Flag'i sıfırla
          setAutoQueryFlag(false);
        }
      };
      
      // Kısa bir gecikme ile çalıştır
      setTimeout(checkAuthAndQuery, 300);
    }
  }, [autoQueryFlag, domain, queryType]);

  // URL'i güncelleme fonksiyonu
  const updateURL = (currentDomain, currentQueryType) => {
    const formTypeMap = {
      'gmail': 'gmail',
      'dns': 'dns',
      'whois': 'whois'
    };

    const params = new URLSearchParams();
    if (currentDomain) params.set('domain', currentDomain);
    if (currentQueryType) params.set('form', formTypeMap[currentQueryType]);
    
    navigate(`?${params.toString()}`, { replace: true });
  };

  const signOutUser = () => {
    signOut(auth);
  };

  const handleQueryTypeChange = (e) => {
    const newType = e.target.value;
    
    // Eğer zaten o tipliyse ve gmail tipiyse, tekrar sorgu yap
    if (queryType === 'gmail' && newType === 'gmail' && domain) {
      performQuery(domain, 'gmail');
      return;
    }
    
    setQueryType(newType);
    if (domain) {
      updateURL(domain, newType);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (domain && !loading) {
      performQuery(domain, queryType);
    }
  };

  const handleDomainChange = (e) => {
    let newDomain = e.target.value;
    setDomain(newDomain);
  };

  // Input'tan seçim yapıldığında
  const handleDomainSelect = (e) => {
    // Eğer zaten yükleme durumundaysa işlemi engelle
    if (loading) return;
    
    let selectedDomain = e.target.value;
    
    // Boş domain kontrolü
    if (!selectedDomain || selectedDomain.trim() === '') {
      return;
    }
    
    // URL'den domain çıkarma
    try {
      if (selectedDomain.includes('://') || selectedDomain.startsWith('www.')) {
        const url = new URL(selectedDomain.includes('://') ? selectedDomain : 'http://' + selectedDomain);
        selectedDomain = url.hostname.replace(/^www\./, '');
      }
    } catch (error) {
      // URL parse edilemezse orijinal değeri kullan
    }

    // Email adresinden domain çıkarma
    if (selectedDomain.includes('@')) {
      selectedDomain = selectedDomain.split('@')[1];
    }

    setDomain(selectedDomain);
    
    // Otomatik sorgu yapma kısmını kaldırdık
    // Artık sadece form submit edildiğinde veya radio butonlar değiştiğinde sorgu yapılacak
  };

  const renderGmailResults = () => {
    if (!results) return null;

    return (
      <div className="results-container">
        <div className="card mb-4">
          <div className="card-body">
            <h2 className="card-title text-center mb-4">Gmail Ayarları Sonuçları:</h2>
            {renderRecordResult('MX Kaydı', results.mx)}
            {renderRecordResult('SPF Kaydı', results.spf)}
            {renderRecordResult('DKIM Kaydı', results.dkim)}
            {renderRecordResult('DMARC Kaydı', results.dmarc)}
          </div>
        </div>
      </div>
    );
  };

  const renderRecordResult = (title, data) => {
    if (!data) return null;

    // Gmail doküman linkleri
    const helpLinks = {
      'MX Kaydı': 'https://support.google.com/a/answer/174125?hl=tr&fl=1&sjid=4224932814164588181-NA#zippy=%2Cgoogle-workspace-mevcut-s%C3%BCr%C3%BCm-ve-sonraki-s%C3%BCr%C3%BCmler',
      'SPF Kaydı': 'https://support.google.com/a/answer/33786?hl=tr&fl=1&sjid=12991273387522755300-NA&visit_id=638761728456712639-1360078130&rd=1',
      'DKIM Kaydı': 'https://support.google.com/a/answer/174124?product_name=UnuFlow&hl=en&visit_id=638761728456712639-1360078130&rd=1&src=supportwidget0&hl=tr',
      'DMARC Kaydı': 'https://support.google.com/a/answer/2466580?hl=tr&fl=1&sjid=12991273387522755300-NA&visit_id=638761728456712639-1360078130&rd=1'
    };

    return (
      <div className="card mb-3">
        <div className="card-header">
          <h3 className="mb-0">
            {title}
            {helpLinks[title] && (
              <a 
                href={helpLinks[title]} 
                target="_blank" 
                rel="noopener noreferrer" 
                className="ms-2 small"
                title="Google Yardım Dokümanı"
              >
                <HelpOutline fontSize="small" />
              </a>
            )}
          </h3>
        </div>
        <div className="card-body">
          <p className={`alert ${data.valid ? 'alert-success' : data.message?.includes('UYARI') ? 'alert-warning' : 'alert-danger'}`}>
            <span className={data.valid ? 'text-success' : data.message?.includes('UYARI') ? 'text-warning' : 'text-danger'}>
              {data.valid ? '✓' : data.message?.includes('UYARI') ? '⚠' : '✗'}
            </span>
            {data.message}
          </p>
          {title === 'MX Kaydı' && data.records && (
            <div className="alert alert-info">
              <strong>Yayınlanan {title}:</strong>
              <pre className="mt-2 mb-0">
                {Array.isArray(data.records) ? (
                  data.records.map((record, index) => (
                    <div key={index}>{record}</div>
                  ))
                ) : (
                  data.records
                )}
              </pre>
            </div>
          )}
          {title === 'SPF Kaydı' && data.records && (
            <div className="alert alert-info">
              <strong>Yayınlanan {title}:</strong>
              <pre className="mt-2 mb-0">
                {Array.isArray(data.records) ? (
                  data.records.map((record, index) => (
                    <div key={index}>{record}</div>
                  ))
                ) : (
                  data.records
                )}
              </pre>
            </div>
          )}
          {title === 'DKIM Kaydı' && (
            <div className="alert alert-info">
              <strong>Yayınlanan {title}:</strong>
              {data.records || data.record ? (
                <>
                  <pre className="mt-2 mb-0">{data.records || data.record}</pre>
                  <div className="text-muted mt-2">
                    <InfoOutlined fontSize="small" className="me-1" />
                    Admin panelden DKIM doğrulamasını başlattığınızdan emin olun.
                  </div>
                </>
              ) : (
                <p className="mt-2 mb-0">Henüz bir DKIM kaydı yayınlanmamış.</p>
              )}
            </div>
          )}
          {title === 'DMARC Kaydı' && (
            <>
              {data.record && (
                <div className="alert alert-info">
                  <strong>Yayınlanan DMARC Kaydı:</strong>
                  <pre className="mt-2 mb-0">{data.record}</pre>
                </div>
              )}
              {data.details && (
                <div className="table-responsive">
                  <table className="table table-hover">
                    <thead className="table-light">
                      <tr>
                        <th>Parametre</th>
                        <th>Değer</th>
                        <th>Açıklama</th>
                        <th>Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.details.map((detail, index) => (
                        <tr key={index} className={detail.error ? 'table-danger' : ''}>
                          <td>{detail.tag}</td>
                          <td>{detail.value}</td>
                          <td>{detail.description.replace('dmarc-raporlari@domain', `dmarc-raporlari@${domain}`)}</td>
                          <td className="text-center">
                            {detail.error ? (
                              <span className="text-danger" title={detail.error}>✗</span>
                            ) : (
                              <span className="text-success">✓</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    );
  };

  const renderDnsResults = () => {
    if (!results) return null;
  
    // Production'da DNS sonuçlarını loglama
    if (process.env.NODE_ENV !== 'production') {
      
    }
  
    const recordTypes = ['NS', 'A', 'AAAA', 'CNAME', 'MX', 'TXT'];
  
    let txtRecords = results.TXT ? [...results.TXT] : [];
  
    if (results.DKIM) {
      Object.entries(results.DKIM).forEach(([selector, record]) => {
        if (record) {
          txtRecords.push(`<span class="dkim-selector">${selector}._domainkey.</span> ${record}`);
        }
      });
    }
  
    if (results.DMARC && results.DMARC.length > 0) {
      txtRecords.push(`<span class="dmarc-record">_dmarc.</span> ${results.DMARC[0]}`);
    }
  
    // MX kayıtları için özel format
    let formattedMXRecords = [];
    if (results.MX && Array.isArray(results.MX)) {
      formattedMXRecords = results.MX.map(record => {
        // Eğer string ise direkt kullan
        if (typeof record === 'string') {
          // Tırnak işaretlerini kaldır
          return record.replace(/"/g, '');
        }
        
        // Eğer objeyse daha okunabilir formata dönüştür
        if (typeof record === 'object') {
          const priority = record.priority !== undefined ? record.priority : '';
          const exchange = record.exchange || record.host || '';
          return `${priority} ${exchange}`.trim();
        }
        
        return record;
      });
    }
  
    const updatedResults = {
      ...results,
      MX: formattedMXRecords.length > 0 ? formattedMXRecords : results.MX,
      TXT: txtRecords
    };
  
    return (
      <div className="dns-results">
        <div className="dns-tabs">
          {recordTypes.map(type => (
            <button
              key={type}
              className={`dns-tab ${activeTab === type ? 'active' : ''}`}
              onClick={() => setActiveTab(type)}
            >
              {type}
            </button>
          ))}
        </div>
        <div className="dns-content">
          {recordTypes.map(type => (
            <div key={type} className={`dns-record ${activeTab === type ? 'active' : ''}`}>
              <h3>{type} Kayıtları</h3>
              <pre>
                {Array.isArray(updatedResults[type]) ? (
                  updatedResults[type].map((record, index) => (
                    <div key={`${type}-${index}`} dangerouslySetInnerHTML={{ __html: 
                      typeof record === 'object' 
                        ? JSON.stringify(record, null, 2)
                          .replace(/[{}[\]]/g, '')
                          .replace(/"([^"]+)":/g, '$1:') // Tırnak işaretlerini kaldır
                          .trim()
                        : record
                    }} />
                  ))
                ) : (
                  <div dangerouslySetInnerHTML={{ __html: 
                    typeof updatedResults[type] === 'object'
                      ? JSON.stringify(updatedResults[type], null, 2)
                        .replace(/[{}[\]]/g, '')
                        .replace(/"([^"]+)":/g, '$1:') // Tırnak işaretlerini kaldır
                        .trim()
                      : updatedResults[type] || 'Kayıt bulunamadı.'
                  }} />
                )}
              </pre>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderWhoisResults = () => {
    if (!results || !results.whoisData) return null;
    
    const whoisResult = results.whoisData;
    
    // Backend'den gelen veriyi frontend formatına dönüştür
    let whoisData = {
      registrant: "Bilgi yok",
      organization: "Bilgi yok",
      email: "Bilgi yok",
      country: "Bilgi yok",
      creationDate: null,
      updatedDate: null,
      expirationDate: null,
      registrar: "Bilgi yok",
      status: "Bilgi yok",
      nameServers: [],
      address: null,
      phone: null,
      fax: null,
      transferStatus: null,
      frozenStatus: null
    };

    // Backend'den gelen veri formatı kontrolü ve bilgileri uygun alanlara atama
    if (whoisResult.parsed && whoisResult.parsed.flatData) {
      const flatData = whoisResult.parsed.flatData;
      whoisData = {
        registrant: flatData["Kayıt Sahibi"] || whoisData.registrant,
        organization: flatData["Kayıt Sahibi Organizasyon"] || whoisData.organization,
        email: flatData["Registrant Email"] || flatData["email"] || whoisData.email,
        country: flatData["Kayıt Sahibi Ülke"] || whoisData.country,
        creationDate: flatData["Oluşturulma Tarihi"] || flatData["Creation Date"] || whoisData.creationDate,
        updatedDate: flatData["Güncelleme Tarihi"] || flatData["Updated Date"] || whoisData.updatedDate,
        expirationDate: flatData["Bitiş Tarihi"] || flatData["Expiration Date"] || whoisData.expirationDate,
        registrar: flatData["Kayıt Şirketi"] || whoisData.registrar,
        status: flatData["Domain Durumu"] || whoisData.status,
        nameServers: whoisResult.parsed.sections?.nameServers || [],
        address: flatData["Adres"] || null,
        phone: flatData["Telefon"] || null,
        fax: flatData["Faks"] || null,
        transferStatus: flatData["Transfer Durumu"] || null,
        frozenStatus: flatData["Dondurulmuş Durum"] || null
      };
    } else if (whoisResult.parsed) {
      // Alternatif format - parsed içindeki doğrudan alanlar
      const parsedData = whoisResult.parsed;
      whoisData = {
        registrant: parsedData["Registrant Name"] || parsedData["registrant"] || parsedData["NIC Handle"] || whoisData.registrant,
        organization: parsedData["Registrant Organization"] || parsedData["organisation"] || parsedData["Organization Name"] || whoisData.organization,
        email: parsedData["Registrant Email"] || parsedData["email"] || whoisData.email,
        country: parsedData["Registrant Country"] || parsedData["country"] || whoisData.country,
        creationDate: parsedData["Creation Date"] || parsedData["Created on"] || parsedData["created"] || parsedData["Created on.............."] || whoisData.creationDate,
        updatedDate: parsedData["Updated Date"] || parsedData["Last updated on"] || parsedData["updated"] || parsedData["Last Update Time"] || whoisData.updatedDate,
        expirationDate: parsedData["Registry Expiry Date"] || parsedData["Expiration Date"] || parsedData["Expires on"] || parsedData["expires"] || parsedData["Expires on.............."] || whoisData.expirationDate,
        registrar: parsedData["Registrar"] || parsedData["registrar"] || whoisData.registrar,
        status: parsedData["Domain Status"] || parsedData["status"] || whoisData.status,
        nameServers: whoisResult.parsed.sections?.nameServers || [],
        address: parsedData["Address"] || whoisData.address,
        phone: parsedData["Phone"] || whoisData.phone,
        fax: parsedData["Fax"] || whoisData.fax,
        transferStatus: parsedData["Transfer Status"] || whoisData.transferStatus,
        frozenStatus: parsedData["Frozen Status"] || whoisData.frozenStatus
      };
    } else if (typeof whoisResult === 'object') {
      // Doğrudan whoisResult içindeki alanlar
      whoisData = {
        registrant: whoisResult.registrant || whoisData.registrant,
        organization: whoisResult.organization || whoisData.organization,
        email: whoisResult.email || whoisData.email,
        country: whoisResult.country || whoisData.country,
        creationDate: whoisResult.creationDate || whoisData.creationDate,
        updatedDate: whoisResult.updatedDate || whoisData.updatedDate,
        expirationDate: whoisResult.expirationDate || whoisData.expirationDate,
        registrar: whoisResult.registrar || whoisData.registrar,
        status: whoisResult.status || whoisData.status,
        nameServers: whoisResult.nameServers || [],
        address: whoisResult.address || whoisData.address,
        phone: whoisResult.phone || whoisData.phone,
        fax: whoisResult.fax || whoisData.fax,
        transferStatus: whoisResult.transferStatus || whoisData.transferStatus,
        frozenStatus: whoisResult.frozenStatus || whoisData.frozenStatus
      };
    }
    
    const convertTimestamps = (obj) => {
      if (!obj) return null;
      const result = {};
      
      for (const key in obj) {
        if (obj[key] instanceof Date) {
          result[key] = obj[key].toLocaleString('tr-TR');
        } else if (typeof obj[key] === 'object' && obj[key] !== null) {
          result[key] = convertTimestamps(obj[key]);
        } else {
          result[key] = obj[key];
        }
      }
      
      return result;
    };
    
    const processedData = convertTimestamps(whoisData);
    
    const getFormattedDate = (dateStr) => {
      if (!dateStr) return 'Bilgi yok';
      try {
        return new Date(dateStr).toLocaleString('tr-TR');
      } catch (e) {
        return dateStr;
      }
    };
    
    return (
      <div className="whois-results">
        <div className="mb-3">
          <h3>WHOIS Bilgileri: {results.domain}</h3>
          <div className="card">
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <p><strong>Kayıt Sahibi:</strong> {whoisData.registrant || 'Bilgi yok'}</p>
                  <p><strong>Organizasyon:</strong> {whoisData.organization || 'Bilgi yok'}</p>
                  <p><strong>E-posta:</strong> {whoisData.email || 'Bilgi yok'}</p>
                  <p><strong>Ülke:</strong> {whoisData.country || 'Bilgi yok'}</p>
                  {whoisData.address && <p><strong>Adres:</strong> {whoisData.address}</p>}
                  {whoisData.phone && <p><strong>Telefon:</strong> {whoisData.phone}</p>}
                  {whoisData.fax && <p><strong>Faks:</strong> {whoisData.fax}</p>}
                </div>
                <div className="col-md-6">
                  <p><strong>Kayıt Tarihi:</strong> {getFormattedDate(whoisData.creationDate)}</p>
                  <p><strong>Son Güncelleme:</strong> {getFormattedDate(whoisData.updatedDate)}</p>
                  <p><strong>Bitiş Tarihi:</strong> {getFormattedDate(whoisData.expirationDate)}</p>
                  <p><strong>Kayıt Kurumu:</strong> {whoisData.registrar || 'Bilgi yok'}</p>
                  {whoisData.transferStatus && <p><strong>Transfer Durumu:</strong> {whoisData.transferStatus}</p>}
                  {whoisData.frozenStatus && <p><strong>Dondurulma Durumu:</strong> {whoisData.frozenStatus}</p>}
                </div>
              </div>
              <div className="mt-3">
                <p><strong>Durum:</strong> {whoisData.status ? (Array.isArray(whoisData.status) ? whoisData.status.join(', ') : whoisData.status) : 'Bilgi yok'}</p>
                <p><strong>Nameservers:</strong> {whoisData.nameServers && whoisData.nameServers.length > 0 ? whoisData.nameServers.join(', ') : 'Bilgi yok'}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderResults = () => {
    if (error) return <p className="error">{error}</p>;
    
    // Sonuç yoksa boş göster
    if (!results) return null;

    // Sonuçları göster ve loading durumunda overlay ekle
    return (
      <div className="results-container">
        {/* Loading overlay */}
        {loading && (
          <div className="results-loading-overlay">
            <CircularProgress size={30} />
          </div>
        )}
        
        {/* Sonuçlar */}
        <div className={loading ? "results results-loading" : "results"}>
          {(() => {
    switch (queryType) {
      case 'gmail':
        return renderGmailResults();
      case 'dns':
        return renderDnsResults();
      case 'whois':
        return renderWhoisResults();
      default:
        return null;
    }
          })()}
        </div>
      </div>
    );
  };

  const toggleDarkMode = async () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);
    if (user) {
      await saveDarkModePreference(user.uid, newDarkMode);
    }
  };

  const handleHomeClick = () => {
    setDomain('');
    setResults(null);
    setError(null);
    setQueryType('gmail');
    navigate('/');
  };

  const handleDelete = async (domain) => {
    try {
      const userId = auth.currentUser?.uid;
      await deleteQuery(userId, domain);
      
      // Tüm tabloyu yeniden yüklemek yerine, sadece silinen öğeyi state'ten kaldır
      setQueries(prevQueries => prevQueries.filter(q => q.domain !== domain));
      
      // Takip edilenlerden de kaldır
      setFollowedQueries(prevQueries => prevQueries.filter(q => q.domain !== domain));
    } catch (err) {
      console.error('Sorgu silinirken hata:', err);
    }
  };

  const handleRefresh = async (domain) => {
    try {
      setRefreshing(prev => ({ ...prev, [domain]: true }));
      const userId = auth.currentUser?.uid;
      const result = await refreshQuery(userId, domain);
      
      // Tüm tabloyu yeniden yüklemek yerine, sadece güncellenen öğeyi state'te güncelle
      setQueries(prevQueries => {
        return prevQueries.map(q => {
          if (q.domain === domain) {
            return {
              ...q,
              ...result,
              lastChecked: new Date()
            };
          }
          return q;
        });
      });
      
      // Takip edilenler listesini de güncelle
      setFollowedQueries(prevQueries => {
        return prevQueries.map(q => {
          if (q.domain === domain) {
            return {
              ...q,
              ...result,
              lastChecked: new Date()
            };
          }
          return q;
        });
      });
    } catch (err) {
      console.error('Sorgu yenilenirken hata:', err);
    } finally {
      setRefreshing(prev => ({ ...prev, [domain]: false }));
    }
  };

  const handleFollowToggle = async (query, isFollowed) => {
    try {
      const userId = auth.currentUser?.uid;
      
      if (isFollowed) {
        await unfollowQuery(userId, query.domain);
        
        // Takip edilenlerden kaldır
        setFollowedQueries(prev => prev.filter(q => q.domain !== query.domain));
        
        // Ana listedeki takip durumunu güncelle
        setQueries(prev => prev.map(q => 
          q.domain === query.domain ? { ...q, isFollowed: false } : q
        ));
      } else {
        await followQuery(userId, query);
        
        // Takip edilenlere ekle
        const updatedQuery = { ...query, isFollowed: true, followedAt: new Date() };
        setFollowedQueries(prev => [...prev, updatedQuery]);
        
        // Ana listedeki takip durumunu güncelle
        setQueries(prev => prev.map(q => 
          q.domain === query.domain ? { ...q, isFollowed: true } : q
        ));
      }
    } catch (err) {
      console.error('Takip işlemi sırasında hata:', err);
    }
  };

  // Tüm takip edilen sorguları yenileme
  const refreshAllFollowed = async () => {
    if (!followedQueries.length) return;
    
    try {
      setLoading(true);
      setRefreshProgress(0);
      
      // Her sorgu için bir Promise oluştur
      const refreshPromises = followedQueries.map((query, index) => {
        // Her sorgu arasında 1 saniye bekle
        return new Promise(resolve => {
          setTimeout(async () => {
            try {
              const result = await refreshQuery(auth.currentUser.uid, query.domain);
              
              // Sorguları tek tek güncelle, tüm listeyi yeniden yükleme
              setFollowedQueries(prev => {
                return prev.map(q => {
                  if (q.domain === query.domain) {
                    return {
                      ...q,
                      ...result,
                      lastChecked: new Date()
                    };
                  }
                  return q;
                });
              });
              
              // Ana listeyi de güncelle
              setQueries(prev => {
                return prev.map(q => {
                  if (q.domain === query.domain) {
                    return {
                      ...q,
                      ...result,
                      lastChecked: new Date()
                    };
                  }
                  return q;
                });
              });
              
              setRefreshProgress(((index + 1) / followedQueries.length) * 100);
              resolve();
            } catch (error) {
              console.error(`${query.domain} sorgulanırken hata:`, error);
              resolve(); // Hata olsa bile devam et
            }
          }, index * 1000); // index * 1000 ms bekle
        });
      });
      
      // Tüm sorguları sırayla yenile
      await Promise.all(refreshPromises);
      
    } catch (error) {
      console.error('Toplu sorgulama sırasında hata:', error);
    } finally {
      setLoading(false);
      setRefreshProgress(0);
    }
  };

  const renderQueryTable = (queryList, isFollowedTab = false) => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Domain</TableCell>
            <TableCell>MX</TableCell>
            <TableCell>SPF</TableCell>
            <TableCell>DKIM</TableCell>
            <TableCell>DMARC</TableCell>
            <TableCell>Son Kontrol</TableCell>
            <TableCell>İşlemler</TableCell>
          </TableRow>
        </TableHead>
        <TableBody className="TableBody-fadeIn">
          {queryList.map((query, index) => (
            <TableRow key={query.domain} className="TableRow-slideIn" style={{ animationDelay: `${index * 0.05}s` }}>
              <TableCell>
                <Link 
                  to={`/home?domain=${query.domain}&form=gmail`}
                  onClick={() => {
                    setActiveMainTab('new');
                    setDomain(query.domain);
                    setQueryType('gmail');
                    performQuery(query.domain, 'gmail');
                  }}
                  style={{ 
                    color: 'inherit', 
                    textDecoration: 'underline', 
                    cursor: loading ? 'wait' : 'pointer',
                    pointerEvents: loading ? 'none' : 'auto'
                  }}
                >
                  {query.domain}
                </Link>
              </TableCell>
              <TableCell>{renderStatusIcon(query.mx)}</TableCell>
              <TableCell>{renderStatusIcon(query.spf)}</TableCell>
              <TableCell>{renderStatusIcon(query.dkim)}</TableCell>
              <TableCell>{renderStatusIcon(query.dmarc)}</TableCell>
              <TableCell>
                <Tooltip title={
                  (() => {
                    try {
                      let date;
                      if (query.lastChecked && typeof query.lastChecked === 'object' && query.lastChecked.seconds) {
                        date = new Date(query.lastChecked.seconds * 1000);
                      } else if (query.lastChecked instanceof Date) {
                        date = query.lastChecked;
                      } else {
                        date = new Date(query.lastChecked);
                      }
                      return date.toLocaleString('tr-TR');
                    } catch (error) {
                      return 'Geçersiz tarih';
                    }
                  })()
                }>
                  <Typography>{formatTimeAgo(query.lastChecked)}</Typography>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  {refreshing[query.domain] ? (
                    <CircularProgress size={24} />
                  ) : (
                    <Tooltip title="Yenile">
                      <span>
                        <IconButton 
                          onClick={() => handleRefresh(query.domain)}
                          disabled={loading || refreshing[query.domain]}
                        >
                          <Refresh />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  <Tooltip title={query.isFollowed ? 'Takibi Bırak' : 'Takip Et'}>
                    <span>
                      <IconButton 
                        onClick={() => handleFollowToggle(query, query.isFollowed)}
                        disabled={loading}
                      >
                        {query.isFollowed ? <Favorite color="error" /> : <FavoriteBorder />}
                      </IconButton>
                    </span>
                  </Tooltip>
                  {!isFollowedTab && (
                    <Tooltip title="Sil">
                      <span>
                        <IconButton 
                          onClick={() => handleDelete(query.domain)}
                          disabled={loading}
                        >
                          <Delete />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Box>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderStatusIcon = (data) => {
    if (!data) return null;

    try {
    let icon = '✗';
    let color = 'error.main';
      let message = '';
    
      if (typeof data === 'object') {
    if (data.valid) {
      icon = '✓';
      color = 'success.main';
    } else if (data.message && data.message.includes('UYARI')) {
      icon = '⚠';
      color = 'warning.main';
        }
        
        message = data.message || '';
      } else {
        return null;
    }

    return (
        <Tooltip title={message}>
        <Typography color={color} sx={{ fontSize: '1.2rem' }}>
          {icon}
        </Typography>
      </Tooltip>
    );
    } catch (error) {
      console.error("Durum ikonu oluşturulurken hata:", error);
      return null;
    }
  };

  const formatTimeAgo = (timestamp) => {
    if (!timestamp) return '';
    
    try {
    const now = new Date();
      let date;
      
      // Firestore Timestamp objesi kontrolü
      if (timestamp && typeof timestamp === 'object' && timestamp.seconds && timestamp.nanoseconds) {
        date = new Date(timestamp.seconds * 1000);
      } else if (timestamp instanceof Date) {
        date = timestamp;
      } else {
        date = new Date(timestamp);
      }
      
      if (isNaN(date.getTime())) {
        return 'Geçersiz tarih';
      }
      
    const diffInSeconds = Math.floor((now - date) / 1000);
    
    if (diffInSeconds < 60) return 'Az önce';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)} dakika önce`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)} saat önce`;
    if (diffInSeconds < 2592000) return `${Math.floor(diffInSeconds / 86400)} gün önce`;
    return `${Math.floor(diffInSeconds / 2592000)} ay önce`;
    } catch (error) {
      console.error("Tarih formatlanırken hata:", error);
      return 'Bilinmeyen tarih';
    }
  };

  // Ana sayfa içeriği
  const renderMainContent = () => {
    if (!user) {
      return (
        <Box p={3}>
          <Alert severity="warning">
            Bu sayfayı görüntülemek için giriş yapmanız gerekmektedir. Lütfen giriş yapın.
          </Alert>
        </Box>
      );
    }

    return (
      <Box sx={{ width: '100%' }}>
        <Tabs value={activeMainTab} onChange={(e, newValue) => setActiveMainTab(newValue)}>
          <Tab label="Yeni Sorgu" value="new" />
          <Tab label="Tüm Sorgularım" value="all" />
          <Tab label="Takip Ettiklerim" value="followed" />
        </Tabs>

        <Box sx={{ mt: 2 }}>
          {activeMainTab === 'new' && (
            <div className="query-section">
              <form onSubmit={handleSubmit} className="bg-white shadow-sm rounded p-4 mb-4">
                <div className="mb-3">
                  <label htmlFor="domain" className="form-label">Domain Adresi:</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      id="domain"
                      value={domain}
                      onChange={handleDomainChange}
                      onBlur={handleDomainSelect}
                      placeholder="example.com"
                      autoComplete="on"
                      list="domain-history"
                      disabled={loading}
                    />
                    <datalist id="domain-history">
                      {queries.map(q => (
                        <option key={q.domain} value={q.domain} />
                      ))}
                    </datalist>
                  </div>
                </div>

                <div className="mb-3">
                  <div className="radio-group">
                    <input
                      type="radio"
                      id="gmail"
                      name="queryType"
                      value="gmail"
                      checked={queryType === 'gmail'}
                      onChange={handleQueryTypeChange}
                      disabled={loading}
                    />
                    <label 
                      htmlFor="gmail" 
                      className={loading ? 'disabled' : ''}
                  >
                    Gmail Ayarları
                    </label>

                    <input
                      type="radio"
                      id="dns"
                      name="queryType"
                      value="dns"
                      checked={queryType === 'dns'}
                      onChange={handleQueryTypeChange}
                      disabled={loading}
                    />
                    <label 
                      htmlFor="dns" 
                      className={loading ? 'disabled' : ''}
                  >
                    DNS Kayıtları
                    </label>

                    <input
                      type="radio"
                      id="whois"
                      name="queryType"
                      value="whois"
                      checked={queryType === 'whois'}
                      onChange={handleQueryTypeChange}
                      disabled={loading}
                    />
                    <label 
                      htmlFor="whois" 
                      className={loading ? 'disabled' : ''}
                  >
                    Whois
                    </label>
                  </div>
                </div>
              </form>

              <div className="results">
                {loading && (
                  <div className="loading-overlay">
                    <div className="loading-bar" style={{ width: '100%' }}></div>
                  </div>
                )}
                {renderResults()}
              </div>
            </div>
          )}

          {activeMainTab === 'all' && (
            <Box>
              {loading ? (
                <Box display="flex" justifyContent="center" p={3}>
                  <CircularProgress />
                </Box>
              ) : queries.length > 0 ? (
                renderQueryTable(queries)
              ) : (
                <Typography variant="body1" sx={{ p: 2 }}>
                  Henüz hiç sorgu yapmadınız.
                </Typography>
              )}
            </Box>
          )}

          {activeMainTab === 'followed' && (
            <Box>
              {loading ? (
                <Box display="flex" flexDirection="column" alignItems="center" p={3} gap={2}>
                  <CircularProgress />
                  {refreshProgress > 0 && (
                    <>
                      <LinearProgress 
                        variant="determinate" 
                        value={refreshProgress} 
                        sx={{ width: '100%', maxWidth: 400 }}
                      />
                      <Typography variant="body2" color="text.secondary">
                        {Math.round(refreshProgress)}% Tamamlandı
                      </Typography>
                    </>
                  )}
                </Box>
              ) : followedQueries.length > 0 ? (
                <>
                  <Box sx={{ mb: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={refreshAllFollowed}
                      disabled={loading}
                      startIcon={<Refresh />}
                    >
                      Tümünü Sorgula
                    </Button>
                  </Box>
                  {renderQueryTable(followedQueries, true)}
                </>
              ) : (
                <Typography variant="body1" sx={{ p: 2 }}>
                  Henüz takip ettiğiniz sorgu bulunmuyor.
                </Typography>
              )}
            </Box>
          )}
        </Box>
      </Box>
    );
  };

  const handleSignIn = async () => {
    try {
      setLoading(true);
      setError(null);
      await signInWithGoogle();
    } catch (error) {
      console.error("Giriş yapılırken hata:", error);
      // Kullanıcı dostu hata mesajı göster
      setError(error.message || "Giriş yapılırken bir hata oluştu. Lütfen tekrar deneyin.");
      
      // Hata durumunda kullanıcıya bilgi ver
      alert(`Giriş hatası: ${error.message || "Bilinmeyen hata"}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = () => {
    signOutUser();
  };

  // Tema değiştirme fonksiyonu
  const toggleTheme = async () => {
    const newMode = theme.palette.mode === 'light' ? 'dark' : 'light';
    setDarkMode(newMode === 'dark');
    
    // Tema tercihini localStorage'a kaydet
    localStorage.setItem('theme-preference', newMode);
    
    // Kullanıcı giriş yapmışsa veritabanına da kaydet
    if (user) {
      try {
        await saveDarkModePreference(user.uid, newMode === 'dark');
        console.log('Tema tercihi veritabanına kaydedildi:', newMode);
      } catch (error) {
        console.error('Tema tercihi veritabanına kaydedilirken hata:', error);
      }
    }
  };

  // Tema tercihini localStorage'dan oku
  useEffect(() => {
    const savedTheme = localStorage.getItem('theme-preference');
    if (savedTheme) {
      setDarkMode(savedTheme === 'dark');
    } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      // Sistem temasını kontrol et
      setDarkMode(true);
    }
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div className={`App ${darkMode ? 'dark-mode' : ''}`}>
        <AppBar position="fixed" color="default" elevation={1}>
          <Toolbar>
            <Typography variant="h6" style={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <Link to="/" onClick={handleHomeClick} style={{ textDecoration: 'none', color: 'inherit', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <img src="/media/logo.png" alt="Logo" style={{ height: '24px', verticalAlign: 'middle' }} />
                <span className="header-title">İmba DNS Checker</span>
              </Link>
            </Typography>
            <Box display="flex" alignItems="center" gap={1}>
              {user && user.isAdmin && (
                <Button
                  component={Link}
                  to="/admin"
                  color="inherit"
                  size="small"
                  startIcon={<AdminPanelSettings />}
                >
                  Admin
                </Button>
              )}
              <IconButton
                onClick={toggleTheme}
                color="inherit"
                size="small"
                title={theme.palette.mode === 'dark' ? 'Açık Tema' : 'Koyu Tema'}
              >
                {theme.palette.mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
              </IconButton>
              {user ? (
                <>
                  <Chip
                    avatar={<Avatar src={user.photoURL} />}
                    label={user.displayName}
                    color="default"
                    variant="outlined"
                    size="small"
                  />
                  <IconButton
                    onClick={handleSignOut}
                    color="inherit"
                    size="small"
                    title="Çıkış Yap"
                  >
                    <ExitToApp />
                  </IconButton>
                </>
              ) : (
                <Button
                  onClick={handleSignIn}
                  color="inherit"
                  size="small"
                  startIcon={<Person />}
                >
                  Giriş Yap
                </Button>
              )}
            </Box>
          </Toolbar>
        </AppBar>
        <Toolbar />

        <Routes>
          <Route path="/" element={<Navigate to="/home" />} />
          <Route path="/home" element={<div className="main-content" style={{ marginTop: '1px' }}>
            <div className="content-wrapper">
              {renderMainContent()}
            </div>
          </div>} />
          <Route
            path="/admin"
            element={
              <RequireAuth adminRequired={true}>
                <AdminPage />
              </RequireAuth>
            }
          />
        </Routes>

        <footer className="footer">
          <div className="footer-content">
            <div>
              © 2022 <a href="https://www.imbacloud.com" target="_blank" rel="noopener noreferrer">imbacloud.com</a> | <a href="/privacy.html" target="_blank">Privacy policy</a>
            </div>
            <div>Version: {packageJson.version}</div>
          </div>
        </footer>
      </div>
    </ThemeProvider>
  );
}

export default App;